import React, { useEffect, useRef } from 'react';

// Shared document lock is reference-counted so switching dual lanes cannot leave
// the body locked or restore metadata while the next lane is still expanded.
const viewportOwners = new Set();
let viewportRestore;
export function lockPlayViewport() {
  const owner = {};
  if (!viewportOwners.size) {
    const overflow = document.body.style.overflow;
    let meta = document.querySelector('meta[name="viewport"]');
    const created = !meta, original = meta?.getAttribute('content');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'viewport'; document.head.appendChild(meta); }
    const content = (original || 'width=device-width, initial-scale=1').replace(/,?\s*viewport-fit\s*=\s*[^,]+/gi, '') + ', viewport-fit=cover';
    meta.setAttribute('content', content); document.body.style.overflow = 'hidden';
    viewportRestore = () => {
      document.body.style.overflow = overflow;
      // Do not overwrite metadata another feature changed while play was open.
      if (meta.getAttribute('content') === content) {
        if (created) meta.remove();
        else if (original === null) meta.removeAttribute('content');
        else meta.setAttribute('content', original);
      }
    };
  }
  viewportOwners.add(owner);
  return () => { if (!viewportOwners.delete(owner)) return; if (!viewportOwners.size) { viewportRestore?.(); viewportRestore = undefined; } };
}

// EmulatorJS/libretro button IDs, not printed SNES labels reused on every console.
export function consoleControls(system) {
  if (system === 'segaMD') return { layout: 'six', face: [[9, 'X'], [10, 'Y'], [11, 'Z'], [8, 'A'], [0, 'B'], [1, 'C']], shoulders: [], menu: [[2, 'Mode'], [3, 'Start']] };
  if (['nes', 'gb', 'gbc', 'gba'].includes(system)) return { layout: 'two', face: [[0, 'B'], [8, 'A']], shoulders: system === 'gba' ? [[10, 'L'], [11, 'R']] : [], menu: [[2, 'Select'], [3, 'Start']] };
  return { layout: 'diamond', face: [[9, 'X'], [1, 'Y'], [8, 'A'], [0, 'B']], shoulders: [[10, 'L'], [11, 'R']], menu: [[2, 'Select'], [3, 'Start']] };
}
export function directionMask(x, y, rect) {
  const dx = (x - rect.left) / rect.width, dy = (y - rect.top) / rect.height;
  return (dx < .34 ? 1 << 6 : dx > .66 ? 1 << 7 : 0) | (dy < .34 ? 1 << 4 : dy > .66 ? 1 << 5 : 0);
}
const DIRECTIONS = [[80, '↖'], [16, '↑'], [144, '↗'], [64, '←'], [0, '•'], [128, '→'], [96, '↙'], [32, '↓'], [160, '↘']];

export default function MobileControls({ system, seat, disabled, touches, sendHeld, canControl, mask }) {
  const pad = useRef(), padPointers = useRef(new Set());
  const handlers = useRef();
  const release = id => {
    padPointers.current.delete(id);
    if (touches.current.delete(id)) sendHeld();
  };
  const start = (event, value, directional = false) => {
    if (!canControl()) return;
    event.preventDefault();
    // Safari may throw for synthetic/missing active pointers. A hold must survive it.
    touches.current.set(event.pointerId, value);
    if (directional) padPointers.current.add(event.pointerId);
    else padPointers.current.delete(event.pointerId);
    sendHeld();
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
  };
  handlers.current = {
    release,
    move: event => {
      if (!padPointers.current.has(event.pointerId) || !touches.current.has(event.pointerId)) return;
      if (!canControl()) return release(event.pointerId);
      if (event.cancelable) event.preventDefault();
      const value = directionMask(event.clientX, event.clientY, pad.current.getBoundingClientRect());
      if (touches.current.get(event.pointerId) !== value) { touches.current.set(event.pointerId, value); sendHeld(); }
    },
  };
  useEffect(() => {
    // Capture-independent release also catches an up outside the original control.
    const up = e => handlers.current.release(e.pointerId), move = e => handlers.current.move(e);
    window.addEventListener('pointerup', up, true); window.addEventListener('pointercancel', up, true);
    window.addEventListener('pointermove', move, { capture: true, passive: false });
    return () => {
      window.removeEventListener('pointerup', up, true); window.removeEventListener('pointercancel', up, true);
      window.removeEventListener('pointermove', move, true);
      padPointers.current.clear();
      // Parent owns touch state and releases it on seat/pause/unmount.
    };
  }, []);
  const config = consoleControls(system);
  const button = ([b, label], extra = '') => <button key={label} type="button" className={'netplay-control ' + extra} disabled={disabled}
    aria-label={'P' + seat + ' ' + label} aria-pressed={Boolean(mask & (1 << b))}
    onPointerDown={e => start(e, 1 << b)} onPointerUp={e => release(e.pointerId)}
    onPointerCancel={e => release(e.pointerId)} onLostPointerCapture={e => release(e.pointerId)}
    onContextMenu={e => e.preventDefault()}>{label}</button>;
  return <div className="netplay-controls" aria-label={'Player ' + seat + ' touch controller'} onContextMenu={e => e.preventDefault()}>
    <div ref={pad} className="netplay-dpad" aria-label="Directional pad"
      onPointerDown={e => { const value = e.target.closest('button')?.dataset.mask; start(e, value === undefined ? directionMask(e.clientX, e.clientY, pad.current.getBoundingClientRect()) : Number(value), true); }}
      onPointerUp={e => release(e.pointerId)} onPointerCancel={e => release(e.pointerId)} onLostPointerCapture={e => release(e.pointerId)}>
      {DIRECTIONS.map(([value, label]) => <button key={label} type="button" className={'netplay-control netplay-direction' + (!value ? ' netplay-pad-center' : '')}
        data-mask={value} disabled={disabled} aria-label={'P' + seat + ' ' + label} aria-pressed={Boolean(value && (mask & value) === value)}>{label}</button>)}
    </div>
    <div className="netplay-action-side">
      {config.shoulders.length > 0 && <div className="netplay-shoulders">{config.shoulders.map(b => button(b))}</div>}
      <div className={'netplay-face netplay-face--' + config.layout}>{config.face.map(b => button(b, 'netplay-face-' + b[1]))}</div>
    </div>
    <div className="netplay-system-buttons">{config.menu.map(b => button(b))}</div>
  </div>;
}
